import { useEffect, useRef, type ReactNode } from 'react';
import { env } from '@/config/env';
import { ANALYTICS_EVENTS, trackEvent } from '@/features/analytics';
import { getOS } from '@/shared/lib/native-browser';
import { SUPPORT_EMAIL } from '@/shared/lib/email';
import appIcon from '../assets/get-app/app-icon.webp';
import badgeAppStore from '../assets/get-app/badge-app-store.webp';
import badgeGooglePlay from '../assets/get-app/badge-google-play.webp';
import step2Preview from '../assets/get-app/step-2.webp';
import { OfferStepperHeader } from './offer-stepper-header';

type StoreSet = 'ios' | 'android' | 'both';

function resolveStoreSet(): StoreSet {
  const os = getOS();
  if (os === 'ios') return 'ios';
  if (os === 'android') return 'android';
  return 'both';
}

function StoreLink({
  href,
  store,
  children,
}: {
  href: string;
  store: 'app_store' | 'play';
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex shrink-0"
      onClick={() => {
        trackEvent(ANALYTICS_EVENTS.SETTLEMENT_GET_APP_BUTTON_CLICK, {
          metadata: { store },
        });
      }}
    >
      {children}
    </a>
  );
}

interface GetAppStepProps {
  offerLabels: string[];
  currentIndex: number;
}

export function GetAppStep({ offerLabels, currentIndex }: GetAppStepProps) {
  const stores = resolveStoreSet();
  const appleUrl = env.VITE_APP_STORE_URL;
  const playUrl = env.VITE_PLAY_STORE_URL;
  const firedShown = useRef(false);

  useEffect(() => {
    if (firedShown.current) return;
    firedShown.current = true;
    trackEvent(ANALYTICS_EVENTS.SETTLEMENT_GET_APP_SHOWN);
  }, []);

  const step1Copy =
    stores === 'ios'
      ? 'Download the app from the App Store'
      : stores === 'android'
        ? 'Download the app from Google Play'
        : 'Download the app from the App Store or Google Play';

  return (
    <div className="min-h-[100dvh] w-full bg-mf-neutral-50 font-body text-mf-neutral-900">
      <div className="mx-auto flex min-h-[100dvh] max-w-app flex-col bg-mf-neutral-50">
        <OfferStepperHeader offerLabels={offerLabels} currentIndex={currentIndex} />

        <div className="flex flex-col gap-6 px-4 pb-10 pt-6">
          <div className="flex flex-col gap-2 text-center">
            <h1 className="font-display text-[28px] font-bold leading-9 text-mf-neutral-900">
              We&apos;ve automatically upgraded your account!
            </h1>
            <p className="font-body text-base font-normal leading-5 text-mf-neutral-600">
              You already have a MoneyFox account, so if you currently have the app installed, just
              open the app! If not, follow the directions below.
            </p>
          </div>

          <section className="flex flex-col items-center gap-4 rounded-[24px] bg-white p-4">
            <div className="flex w-full flex-col items-center gap-1">
              <p className="font-display text-xl font-bold leading-6 text-mf-neutral-900">Step 1</p>
              <p className="text-center font-body text-base font-normal leading-5 text-mf-neutral-600">{step1Copy}</p>
            </div>
            <div className="flex w-full max-w-[324px] flex-col items-center gap-2 rounded-[20px] bg-mf-neutral-50 px-5 py-4">
              <img src={appIcon} alt="" className="size-20 rounded-[18px] object-contain" />
              <div className="flex flex-col items-center">
                <p className="font-body text-base font-bold leading-5 text-mf-neutral-900">MoneyFox</p>
                <p className="font-body text-sm leading-4 text-mf-neutral-600">Settlements &amp; Spend Tracker</p>
              </div>
              <div className="flex items-center justify-center gap-2">
                {stores !== 'android' && (
                  <StoreLink href={appleUrl} store="app_store">
                    <img src={badgeAppStore} alt="Download on the App Store" className="h-11 w-[138px] object-contain" />
                  </StoreLink>
                )}
                {stores !== 'ios' && (
                  <StoreLink href={playUrl} store="play">
                    <img src={badgeGooglePlay} alt="Get it on Google Play" className="h-11 w-[138px] object-contain" />
                  </StoreLink>
                )}
              </div>
            </div>
          </section>

          <section className="flex flex-col items-center gap-4 overflow-hidden rounded-[24px] bg-white p-4">
            <div className="flex w-full flex-col items-center gap-1">
              <p className="font-display text-xl font-bold leading-6 text-mf-neutral-900">Step 2</p>
              <p className="text-center font-body text-base font-normal leading-5 text-mf-neutral-600">
                Open it and sign in to your account
              </p>
            </div>
            <div className="w-full overflow-hidden">
              <img
                src={step2Preview}
                alt=""
                className="h-[149px] w-full object-cover object-top"
              />
            </div>
          </section>

          <p className="text-center font-body text-base font-normal leading-5 text-mf-neutral-600">
            Got a question? Reach out to our{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-mf-green underline decoration-solid"
            >
              support team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
